import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User, AuthState, LoginPayload } from "../../types";
import {
  AUTH_USER_SCHEMA_VERSION,
  normalizeAuthUser,
} from "@/utils/accessControl";

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
};

// Load initial state from localStorage
const loadAuthFromStorage = (): Partial<AuthState> => {
  try {
    const token = localStorage.getItem("authToken");
    const userData = localStorage.getItem("userData");

    if (token && userData) {
      const parsedUser = JSON.parse(userData) as User;
      const user =
        parsedUser.auth_schema_version === AUTH_USER_SCHEMA_VERSION
          ? parsedUser
          : normalizeAuthUser(parsedUser);
      return {
        user,
        token,
        isAuthenticated: true,
        loading: false,
      };
    }
  } catch (error) {
    console.error("Error loading auth from storage:", error);
  }

  return {
    loading: false,
  };
};

const authSlice = createSlice({
  name: "auth",
  initialState: {
    ...initialState,
    ...loadAuthFromStorage(),
  },
  reducers: {
    login: (state, action: PayloadAction<LoginPayload>) => {
      state.user = normalizeAuthUser(action.payload.user);
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.loading = false;

      // Clear any previous user data to ensure only one user is logged in at a time
      localStorage.removeItem("previous_user");
      localStorage.removeItem("custom_previous_user_id");
      localStorage.removeItem("token"); // Clear old token if exists

      // Save new user data to localStorage
      localStorage.setItem("authToken", action.payload.token);
      localStorage.setItem("userData", JSON.stringify(action.payload.user));
    },
    logout: (state) => {
      // Save current user as previous_user before clearing (for potential recovery)
      const currentUser = state.user;
      if (currentUser) {
        const custom_previous_user_id = localStorage.getItem(
          "custom_previous_user_id",
        );
        const previous_user = custom_previous_user_id
          ? { id: custom_previous_user_id }
          : currentUser;
        localStorage.setItem("previous_user", JSON.stringify(previous_user));
      }

      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;

      // Clear current user's auth data from localStorage
      localStorage.removeItem("authToken");
      localStorage.removeItem("userData");
      localStorage.removeItem("token"); // Clear old token if exists
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem("userData", JSON.stringify(state.user));
      }
    },
  },
});

export const { login, logout, setLoading, updateUser } = authSlice.actions;
export default authSlice.reducer;
